import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { PlanbookEditBasics } from "@shared/models/analytics/planbook"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "../../action-doc-builder"
import { partial } from "lodash-es"
import type { Narrative } from "../create-narrative"
import defaultPlanbookActivity from "./default-planbook-activity"
import defaultPlanbookContext from "./default-planbook-context"
import defaultPlanbookModels from "./default-planbook-models"

interface Payload {
  planbookId: string
  cover: string
  originalCover: string
  backgroundType: "color" | "image" | "unsplash" | "filestack"
}

export default function planbookChangeCoverAction(payload: Payload): PreparedAction {
  return {
    name: "PLANBOOK_CHANGE_COVER",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  let patch = Patch.create("planbook", payload.planbookId).set("attributes.cover", payload.cover)

  return [patch.patch]
}

function undoPatches(payload: Payload) {
  let patch = Patch.create("planbook", payload.planbookId).set(
    "attributes.cover",
    payload.originalCover
  )

  return [patch.patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  const models = defaultPlanbookModels(payload, findFn, userId, payload.planbookId)

  const adverbialVerbByType = {
    unsplash: "searching",
    filestack: "uploading",
    image: "selecting",
    color: "selecting",
  } as const

  const adverbialObjectByType = {
    unsplash: "image",
    filestack: "image",
    image: "image",
    color: "color",
  } as const

  const adverbial_verb = adverbialVerbByType[payload.backgroundType]
  const adverbial_object_type = adverbialObjectByType[payload.backgroundType]

  const newEvent: PlanbookEditBasics = {
    ...createBlankEvent(),
    root_id: payload.planbookId,
    root_type: "planbook",
    primary_object_id: payload.planbookId,
    primary_object_id_and_ancestor_ids: [payload.planbookId],
    primary_object_type: "planbook",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),
    primary_verb: "edit",
    primary_object_specifier: "cover",
    adverbial_verb,
    adverbial_object_type,
    adverbial_object_specifier: null,
  }

  return {
    title: "Changed Planbook Background",
    titleForAnalytics: "Planbook Cover - Change",
    description: `Changed background of your ${models.planbook.attributes.title} planbook by ${adverbial_verb} ${adverbial_object_type}`,
    context: defaultPlanbookContext(payload, models),
    activity: defaultPlanbookActivity(payload, models),
    newAnalyticsEvents: [newEvent],
  }
}
